import { useState } from "react";
import { Button, Checkbox, DatePicker, Input } from "antd";
import type { Moment } from "moment";
import "./ProfileIdentitySection.scss";

const { TextArea } = Input;

const BUSINESS_CATEGORIES = ["Restaurant", "Hospitality", "Retail"];

interface IdentityForm {
  businessName: string;
  alternateNames: string;
  street: string;
  street2: string;
  city: string;
  state: string;
  zip: string;
  country: string;
  latitude: string;
  longitude: string;
  primaryPhone: string;
  secondaryPhone: string;
  website: string;
  descriptionShort: string;
  descriptionLong: string;
  categories: string[];
  openingDate: Moment | null;
}

const EMPTY: IdentityForm = {
  businessName: "",
  alternateNames: "",
  street: "",
  street2: "",
  city: "",
  state: "",
  zip: "",
  country: "",
  latitude: "",
  longitude: "",
  primaryPhone: "",
  secondaryPhone: "",
  website: "",
  descriptionShort: "",
  descriptionLong: "",
  categories: [],
  openingDate: null,
};

export function ProfileIdentitySection() {
  const [form, setForm] = useState<IdentityForm>(EMPTY);
  const [savedSnapshot, setSavedSnapshot] = useState<IdentityForm>(EMPTY);

  function update<K extends keyof IdentityForm>(key: K, value: IdentityForm[K]) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  function toggleCategory(category: string, checked: boolean) {
    setForm((f) => ({
      ...f,
      categories: checked
        ? [...f.categories, category]
        : f.categories.filter((c) => c !== category),
    }));
  }

  function discardChanges() {
    setForm(savedSnapshot);
  }

  function saveChanges() {
    setSavedSnapshot(form);
  }

  return (
    <div className="profile-identity">
      <section className="pi-group">
        <h3 className="pi-group-title">Business Identity</h3>

        <Field label="Business Name">
          <Input
            placeholder="Business Name"
            size="large"
            value={form.businessName}
            onChange={(e) => update("businessName", e.target.value)}
          />
        </Field>

        <Field label="Alternate Business Names">
          <Input
            placeholder="Alternate Business Names"
            size="large"
            value={form.alternateNames}
            onChange={(e) => update("alternateNames", e.target.value)}
          />
        </Field>
      </section>

      <section className="pi-group">
        <h3 className="pi-group-title">Location</h3>

        <Field label="Street Address">
          <Input
            placeholder="Address"
            size="large"
            value={form.street}
            onChange={(e) => update("street", e.target.value)}
          />
        </Field>

        <Field label="Street Address Line 2 (Optional)">
          <Input
            placeholder="Address Line 2"
            size="large"
            value={form.street2}
            onChange={(e) => update("street2", e.target.value)}
          />
        </Field>

        <div className="pi-row">
          <Field label="City">
            <Input
              placeholder="City"
              size="large"
              value={form.city}
              onChange={(e) => update("city", e.target.value)}
            />
          </Field>
          <Field label="State">
            <Input
              placeholder="State"
              size="large"
              value={form.state}
              onChange={(e) => update("state", e.target.value)}
            />
          </Field>
        </div>

        <div className="pi-row">
          <Field label="Zip/Postal Code">
            <Input
              placeholder="Zip/Postal Code"
              size="large"
              value={form.zip}
              onChange={(e) => update("zip", e.target.value)}
            />
          </Field>
          <Field label="Country">
            <Input
              placeholder="Country"
              size="large"
              value={form.country}
              onChange={(e) => update("country", e.target.value)}
            />
          </Field>
        </div>

        <div className="pi-row">
          <Field label="Latitude">
            <Input
              placeholder="Latitude"
              size="large"
              value={form.latitude}
              onChange={(e) => update("latitude", e.target.value)}
            />
          </Field>
          <Field label="Longitude">
            <Input
              placeholder="Longitude"
              size="large"
              value={form.longitude}
              onChange={(e) => update("longitude", e.target.value)}
            />
          </Field>
        </div>
      </section>

      <section className="pi-group">
        <h3 className="pi-group-title">Contact</h3>

        <div className="pi-row">
          <Field label="Primary Phone">
            <Input
              placeholder="Primary Phone"
              size="large"
              value={form.primaryPhone}
              onChange={(e) => update("primaryPhone", e.target.value)}
            />
          </Field>
          <Field label="Secondary Phone">
            <Input
              placeholder="Secondary Phone"
              size="large"
              value={form.secondaryPhone}
              onChange={(e) => update("secondaryPhone", e.target.value)}
            />
          </Field>
        </div>

        <Field label="Website">
          <Input
            placeholder="Website"
            size="large"
            value={form.website}
            onChange={(e) => update("website", e.target.value)}
          />
        </Field>
      </section>

      <section className="pi-group">
        <h3 className="pi-group-title">About Your Business</h3>

        <Field label="Business Description (Short)">
          <Input
            placeholder="Business Description (Short)"
            size="large"
            value={form.descriptionShort}
            onChange={(e) => update("descriptionShort", e.target.value)}
          />
        </Field>

        <Field label="Business Description (Long)">
          <TextArea
            placeholder="Business Description (Long)"
            rows={5}
            value={form.descriptionLong}
            onChange={(e) => update("descriptionLong", e.target.value)}
          />
        </Field>

        <div className="pi-categories">
          <span className="pi-field-label">Business Categories</span>
          <div className="pi-categories-list">
            {BUSINESS_CATEGORIES.map((cat) => (
              <Checkbox
                key={cat}
                checked={form.categories.includes(cat)}
                onChange={(e) => toggleCategory(cat, e.target.checked)}
              >
                {cat}
              </Checkbox>
            ))}
          </div>
        </div>

        <Field label="Opening Date (Where supported)">
          <DatePicker
            placeholder="Opening Date"
            size="large"
            className="pi-date-picker"
            value={form.openingDate}
            onChange={(d) => update("openingDate", d)}
            format="MMM D, YYYY"
          />
        </Field>
      </section>

      <footer className="pi-actions">
        <Button className="pi-discard-btn" size="large" onClick={discardChanges}>
          Discard Changes
        </Button>
        <Button
          type="primary"
          danger
          className="pi-save-btn"
          size="large"
          onClick={saveChanges}
        >
          Save Changes
        </Button>
      </footer>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="pi-field">
      <span className="pi-field-label">{label}</span>
      {children}
    </label>
  );
}
